import { useCallback } from 'react';
import { tz } from '@date-fns/tz';
import { format } from 'date-fns';
import { useNavigate } from 'react-router';
import Box from '@common/components/Box';
import Button from '@common/components/controls/Button';
import PopUp from '@common/components/PopUp';
import Translated from '@common/components/Translated';
import { type IanaId } from '@common/types/brandedTypes';
import { timeFormat } from '@common/utils/datetime/dateTimeFormats';
import { CurrentBrowserIanaId } from '@common/utils/datetime/timezones';

interface TimezoneInconsistencyPopupProps {
  userIanaId: IanaId;
  onClose: () => void;
  onDontShowAgainForThisTimezone: () => void;
}

function TimezoneInconsistencyPopup({
  userIanaId,
  onClose,
  onDontShowAgainForThisTimezone,
}: Readonly<TimezoneInconsistencyPopupProps>) {
  const navigate = useNavigate();

  const epoch = Date.now();
  const systemTime = format(epoch, timeFormat, { in: tz(CurrentBrowserIanaId) });
  const userTime = format(epoch, timeFormat, { in: tz(userIanaId) });

  const editSettings = useCallback(() => {
    onClose();
    navigate('/account');
  }, [navigate, onClose]);

  return (
    <PopUp
      title={<Translated id="user.timezone.differs.title" />}
      actionButton={(
        <>
          <Button
            onClick={editSettings}
            data-testid="confirmButton"
            disableAutoCapture
          >
            <Translated id="user.timezone.differs.button" />
          </Button>
          <Button
            mode="text"
            onClick={onDontShowAgainForThisTimezone}
            data-testid="dontShowAgainButton"
          >
            <Translated id="user.timezone.dont-show.button" />
          </Button>
        </>
      )}
      close={onClose}
      type="narrow"
    >
      <Box my={4}>
        <Translated id="user.timezone.system-setting" values={{ time: systemTime }} tagName="p" />
        <Translated id="user.timezone.user-setting" values={{ time: userTime }} tagName="p" />
      </Box>
    </PopUp>
  );
}

export default TimezoneInconsistencyPopup;
